import React from 'react';
import { CompanyNameInput } from '../../components/companyNameInput';
import { formatText, HtmlText } from 'core/i18n';
import HasPEP from 'components/hasPEP/hasPEP';
import { TaxationQuestions } from 'components/taxationQuestions/taxationQuestions';
import AccountMoney from 'components/accountMoney/accountMoney';
import { accountUse as AccountUse } from 'components/accountUse/accountUse';
import BlockWrapper from 'components/blockWrapper/blockWrapper';
import Patrimony from 'components/incomes/patrimony/patrimony';
import { Cnae } from 'components/professionTypes/cnae';
import AnnualIncome from '../../questionBlock/AnnualIncome';
import ConditionalToggling from 'utilities/toggling/ConditionalToggling/ConditionalToggling';
import { checkUserInvestmentTypeAndReturnActiveOne } from 'utilities/userProfile';
import { recordEventCustomOnly } from 'utilities/uTagging';
import { CREDIT_CARD, LOAN } from 'constants/userProfile';
import { Fragment } from 'react';
import { FiocTgssConsent } from '../../components/FiocTgssConsent';
import MessageBox from 'components/commons/MessageBox/MessageBox';

export const DefaultQuestions = ({
    commonList,
    disabled,
    formActions,
    onHandleSave,
    shouldRenderTgssQuestion,
    setShowCompanyNameWarning,
    showCompanyNameWarning,
    userProfile,
    userProfileForm,
    ...props
}) => {
    const {
        investmentType,
        is_patrimony,
        profession_id,
        selfEmployed_accountMoney,
        selfEmployed_accountUse,
        selfEmployed_annualIncomeAmount,
        selfEmployed_company,
        selfEmployed_publicCharges,
    } = userProfile;

    const userInvestmentType = checkUserInvestmentTypeAndReturnActiveOne(investmentType);

    return (
        <section className="fioc-account-type">
            <BlockWrapper position={0} visible={true} cancelDefaultFocusBehavior>
                <div className="fioc-account-type__cnae">
                    <Cnae
                        disabled={disabled}
                        formActions={formActions}
                        handleSave={onHandleSave}
                        onActivityChange={() =>
                            recordEventCustomOnly({
                                accionEvento: 'actividad profesional',
                                categoriaEvento: 'interaccion en tu actividad',
                            })
                        }
                        onFieldChange={() =>
                            recordEventCustomOnly({
                                accionEvento: 'sector profesional',
                                categoriaEvento: 'interaccion en tu actividad',
                            })
                        }
                        onKnowCnaeChange={value =>
                            recordEventCustomOnly({
                                accionEvento: 'conoces el cnae',
                                categoriaEvento: 'interaccion en tu actividad',
                                etiquetaEvento: value,
                            })
                        }
                        {...props}
                    />
                </div>
            </BlockWrapper>

            <BlockWrapper
                position={1}
                visible={false}
                shouldKeepVisibleAfter={profession_id || selfEmployed_company || selfEmployed_annualIncomeAmount}
            >
                <div className="fioc-account-type__company-name">
                    <CompanyNameInput
                        formActions={formActions}
                        handleOnFocus={() => {
                            setShowCompanyNameWarning(true);
                            recordEventCustomOnly({
                                accionEvento: 'nombre de la empresa',
                                categoriaEvento: 'interaccion en tu actividad',
                            });
                        }}
                        isValidated={() => {
                            setShowCompanyNameWarning(false);
                            onHandleSave('companyName', selfEmployed_company);
                        }}
                        label={formatText('field-tradename')}
                        profileModel="userProfile.selfEmployed_company"
                        userProfileForm={userProfileForm}
                        userProfile={userProfile}
                    />
                </div>
                {showCompanyNameWarning && (
                    <div className="fioc-account-type__messages__company-name">
                        <MessageBox
                            boxColor="yellow"
                            iconColor="warning-yellow"
                            titleText={formatText('field-companyNameWarningTitle')}
                            contentText={HtmlText(formatText('field-companyNameWarningDescription'))}
                        />
                    </div>
                )}
            </BlockWrapper>

            <BlockWrapper
                position={2}
                visible={false}
                shouldKeepVisibleAfter={selfEmployed_company || selfEmployed_annualIncomeAmount}
            >
                <div className="fioc-account-type__anual-income">
                    <AnnualIncome
                        disabled={disabled}
                        formActions={formActions}
                        handleSave={onHandleSave}
                        model={'selfEmployed'}
                        onChange={() =>
                            recordEventCustomOnly({
                                accionEvento: 'ingresos',
                                categoriaEvento: 'interaccion en tu actividad',
                            })
                        }
                        optionsList={commonList}
                        userProfile={userProfile}
                        {...props}
                    />
                </div>
            </BlockWrapper>

            <ConditionalToggling condition={!userInvestmentType[CREDIT_CARD] && !userInvestmentType[LOAN]}>
                <Fragment>
                    <BlockWrapper position={3} visible={false} firstValue={selfEmployed_annualIncomeAmount}>
                        <div className="fioc-account-type__patrimony">
                            <Patrimony
                                handleSave={onHandleSave}
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                formActions={formActions}
                                disabled={disabled}
                            />
                        </div>
                    </BlockWrapper>
                    <BlockWrapper position={4} visible={false} firstValue={is_patrimony}>
                        <div className="fioc-account-type__has-pep">
                            <HasPEP
                                disabled={disabled}
                                formActions={formActions}
                                handleSave={onHandleSave}
                                profileModel="userProfile.selfEmployed_publicCharges"
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                {...props}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper position={5} visible={false} firstValue={selfEmployed_publicCharges}>
                        <div className="fioc-account-type__account-money">
                            <AccountMoney
                                formActions={formActions}
                                handleSave={onHandleSave}
                                profileModel="userProfile.selfEmployed_accountMoney"
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                {...props}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper position={6} visible={false} firstValue={selfEmployed_accountMoney}>
                        <div className="fioc-account-type__account-use">
                            <AccountUse
                                accountTypes={['personal', 'professional', 'other']}
                                disabled={disabled}
                                formActions={formActions}
                                handleSave={onHandleSave}
                                hasPEP={userProfile['selfEmployed_publicCharges']}
                                profileModel="userProfile.selfEmployed_accountUse"
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                {...props}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper position={7} visible={false} firstValue={selfEmployed_accountUse}>
                        <div className="fioc-account-type__taxation-questions">
                            <TaxationQuestions
                                disabled={disabled}
                                formActions={formActions}
                                handleSave={onHandleSave}
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                            />
                        </div>
                    </BlockWrapper>

                    {shouldRenderTgssQuestion && (
                        <BlockWrapper position={7} visible={false} firstValue={selfEmployed_accountUse}>
                            <div className="fioc-account-type__tgss-consent">
                                <FiocTgssConsent
                                    formActions={formActions}
                                    userProfileForm={userProfileForm}
                                    userProfile={userProfile}
                                />
                            </div>
                        </BlockWrapper>
                    )}
                </Fragment>
            </ConditionalToggling>
        </section>
    );
};
